import React, { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import ReducerConstants from '../../../../_shared/constants/reducers';
import UserService from '../../../../services/UserService';
import MixpanelConfig from '../../../../_shared/constants/mixpanelConfig';
import { BidSource } from '../../../../_shared/types/auction';
import NegotiationModalContainer from '../../../Modal/NegotiationModalContainer';
import {
  TradeContext,
  TradeState,
  TradeStateNew
} from '../../../../_shared/types/trade';
import convertToCurrency from '../../../../_shared/utils/convertToCurrency';
import TutorialNegotiationModal from '../../../Modal/TutorialNegotiationModal';
import NowInNewState from './NowInNewState';
import SendToClosingContainer from '../../../ModalButton/NegotiationButton/SendToClosingContainer';
import { NegotiationContainerListView } from '../../../Vdp/BidBox/Controls/Styles';
import {
  BidInfoTitle,
  BidInfoValue,
  ProxyInfoTitle,
  ProxyInfoValue,
  Column,
  ExternalContainer,
  LeftContainer,
  Row,
  StyledSecondaryButton,
  StyledPrimaryButton
} from '../../PendingNegotiationCard/Styles';
import NegotiationService from '../../../../services/NegotiationService';
import NegotiationErrorModalContainer from '../../../ModalButton/NegotiationButton/NegotiationErrorModalContainer';
import NowInNewStateVariants from '../../../../_shared/constants/nowInNewStateVariants';
import useNegotiationTutorialVariant from '../../../../_shared/hooks/trade/useNegotiationTutorialVariant';
import { StyledTip } from './Styles';
import useAnalytics, {
  getSellerProperties,
  getVinBasicProperties
} from '../../../../_shared/hooks/useAnalytics';
import SegmentConfig from '../../../../_shared/constants/segmentConfig';

const PendingDecisionFooter = ({
  testId,
  tradeSummary,
  tradeContext,
  disableActionButtons,
  isOmp = false,
  handleNavigateToTradeUri
}) => {
  const track = useAnalytics();
  const userService = UserService.get();
  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );
  const {
    id: tradeId,
    topBid,
    negotiationOffer,
    sellerNegotiated,
    isGreyOut,
    tradeState: currentState,
    associatedTradeId,
    isInternalTrade,
    negotiationState,
    sellingNetworkId,
    unusedProxyAmount
  } = tradeSummary;
  const [
    tutorialNegotiationModalIsOpen,
    setTutorialNegotiationModalIsOpen
  ] = useState(false);

  const [negotiationModalIsOpen, setNegotiationModalIsOpen] = useState(false);
  const [
    isNegotiationErrorModalOpen,
    setIsNegotiationErrorModalOpen
  ] = useState(false);
  const [negotiationErrorMessage, setNegotiationErrorMessage] = useState(null);

  const showProxyTip =
    !disableActionButtons &&
    tradeContext === TradeContext.Buy &&
    Number.isInteger(unusedProxyAmount) &&
    unusedProxyAmount > topBid;

  const onOpenNegotiationModal = async () => {
    const negotiationService = NegotiationService.get();
    negotiationService.getNegotiationMessage(
      associatedTradeId,
      tradeContext,
      setNegotiationErrorMessage,
      setIsNegotiationErrorModalOpen,
      setTutorialNegotiationModalIsOpen
    );

    track(MixpanelConfig.names.negotiate, {
      user_type: userService.isSeller(user, tradeSummary?.dealer?.id)
        ? MixpanelConfig.userType.seller
        : MixpanelConfig.userType.buyer,
      source: BidSource.LIST_VIEW,
      trade_id: tradeSummary?.id,
      auction_id: tradeSummary?.auctionId
    });
    track(SegmentConfig.names.pendingNegotiateClicked, {
      ...getVinBasicProperties(tradeSummary),
      ...getSellerProperties(tradeSummary),
      event_state_type: userService.isSeller(user, tradeSummary?.dealer?.id)
        ? SegmentConfig.eventStateType.sellPending
        : SegmentConfig.eventStateType.buyPending
    });
  };

  const getSendToClosingButton = onClick => {
    const message = <FormattedMessage id="list.card.negotiation.sendClosing" />;
    const StyledButton = isOmp ? StyledPrimaryButton : StyledSecondaryButton;
    return <StyledButton onClick={onClick}>{message}</StyledButton>;
  };

  const onCloseTutorialNegotiationModal = () => {
    setTutorialNegotiationModalIsOpen(false);
  };

  const onStartNegotiation = () => {
    setTutorialNegotiationModalIsOpen(false);
    setNegotiationModalIsOpen(true);
  };
  const onCloseNegotiationModal = () => {
    setNegotiationModalIsOpen(false);
  };

  const [, tutorialVariant] = useNegotiationTutorialVariant(
    tradeContext,
    negotiationState,
    sellingNetworkId,
    associatedTradeId,
    isInternalTrade
  );

  const getBidLabelByContext = context => {
    switch (context) {
      case TradeContext.Buy:
        return <FormattedMessage id="list.card.negotiation.yourTopBid" />;
      case TradeContext.Sell:
        return <FormattedMessage id="list.card.negotiation.topBid" />;
      default:
        return null;
    }
  };

  const getFooterButtonsByAuctionState = () => {
    // using isGreyOut from API which tells us when to grey out the trade card
    if (isGreyOut) {
      // "Now in Active auction"
      const nowInActive =
        tradeContext === TradeContext.Buy && currentState === TradeState.active;
      // "Now in upcoming"
      const nowInUpcoming =
        tradeContext === TradeContext.Buy &&
        currentState === TradeStateNew.UPCOMING;

      if (nowInActive) {
        return (
          <NowInNewState
            tradeId={tradeId}
            variant={NowInNewStateVariants.IN_ACTIVE}
            tradeContext={tradeContext}
            handleNavigateToTradeUri={handleNavigateToTradeUri}
            isOmp={isOmp}
          />
        );
      }
      if (nowInUpcoming) {
        return (
          <NowInNewState
            tradeId={tradeId}
            variant={NowInNewStateVariants.IN_UPCOMING}
            tradeContext={tradeContext}
            handleNavigateToTradeUri={handleNavigateToTradeUri}
            isOmp={isOmp}
          />
        );
      }
    }

    // NEGOTIATE button, canSendToClosing -> SEND TO CLOSING button
    const canSendToClosing =
      !isGreyOut && tradeContext === TradeContext.Sell && !sellerNegotiated;
    return (
      <>
        {(!disableActionButtons || tradeContext !== TradeContext.Buy) && (
          <StyledSecondaryButton
            onClick={onOpenNegotiationModal}
            disabled={disableActionButtons}
          >
            <FormattedMessage id="list.card.negotiation.start" />
          </StyledSecondaryButton>
        )}
        {canSendToClosing && (
          <NegotiationContainerListView marginRight>
            <SendToClosingContainer
              trade={tradeSummary}
              topBid={topBid}
              Button={({ onClick }) => getSendToClosingButton(onClick)}
              isInternalTrade={isInternalTrade}
              onStartNegotiation={onStartNegotiation}
            />
          </NegotiationContainerListView>
        )}
      </>
    );
  };

  return (
    <ExternalContainer data-testid={testId}>
      <Row>
        <LeftContainer>
          <Row>
            <Column>
              <BidInfoTitle>{getBidLabelByContext(tradeContext)}</BidInfoTitle>
              <BidInfoValue highlightBidValue>
                {convertToCurrency(topBid)}
              </BidInfoValue>
            </Column>
            {showProxyTip && (
              <Column>
                <ProxyInfoTitle>
                  <FormattedMessage id="your.proxy" />
                </ProxyInfoTitle>
                <ProxyInfoValue highlightBidValue>
                  {convertToCurrency(unusedProxyAmount)}
                </ProxyInfoValue>
              </Column>
            )}
            {negotiationOffer && (
              <Column>
                <BidInfoTitle>
                  <FormattedMessage id="vdp.pending.buyer.label.newAsking" />
                </BidInfoTitle>
                <BidInfoValue>
                  {convertToCurrency(negotiationOffer)}
                </BidInfoValue>
              </Column>
            )}
          </Row>
        </LeftContainer>
        <Row>
          {showProxyTip && (
            <StyledTip>
              <FormattedMessage
                id="negotiation.tip.offer.proxy"
                values={{ br: <br /> }}
              />
            </StyledTip>
          )}
          {getFooterButtonsByAuctionState()}
        </Row>
      </Row>
      {isNegotiationErrorModalOpen ? (
        <NegotiationErrorModalContainer
          isNegotiationErrorModalOpen={isNegotiationErrorModalOpen}
          negotiationErrorMessage={negotiationErrorMessage}
          setIsNegotiationErrorModalOpen={setIsNegotiationErrorModalOpen}
          tradeId={tradeSummary?.id}
          auctionId={tradeSummary?.auctionId}
        />
      ) : (
        <TutorialNegotiationModal
          modalIsOpen={tutorialNegotiationModalIsOpen}
          onClose={onCloseTutorialNegotiationModal}
          onStartNegotiation={onStartNegotiation}
          variant={tutorialVariant}
          isOmp={isOmp}
        />
      )}

      <NegotiationModalContainer
        trade={tradeSummary}
        modalIsOpen={negotiationModalIsOpen}
        tradeContext={tradeContext}
        onClose={onCloseNegotiationModal}
        isOmp={isOmp}
      />
    </ExternalContainer>
  );
};
export default PendingDecisionFooter;
